import { useContext, useState } from "react";
import { ErrorDialogContext, errorHandledAsync } from "@/shared/context/error-context";
import { GetKCloudIntegrationService } from "@/features/accounts/account-group/services/get-kcloud-integration-service";
import { DisconnectKCloudService } from "@/features/accounts/account-group/services/disconnect-kcloud-service";
import { KCloudAuthenticateService } from "@/features/accounts/account-group/services/kcloud-authenticate-service";
import type { ClientInfo as ApiClientInfo} from "@/features/accounts/account-group/services/kcloud-authenticate-model";


interface UseKCloudIntegrationProps {
  accountGroupId: string;
}

export interface KCloudIntegrationInfo {
  accountGroupId: string;
  kikCode: string;
  userId: string;
  uniClientCode: number;
  clientCode: string;
  serviceType: number;
}

export interface ClientInfo {
  uniClientCode: number;
  clientCode: string;
  clientName: string;
  tantoFlag: number;
}

export const useKCloudIntegration = ({ accountGroupId }: UseKCloudIntegrationProps) => {
  const { setError } = useContext(ErrorDialogContext);
  
  const [serviceIntegrationInfo, setServiceIntegrationInfo] = useState<KCloudIntegrationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getKCloudIntegrationService = new GetKCloudIntegrationService();
  const disconnectKCloudService = new DisconnectKCloudService();

  /**
   * サービス連携情報を取得
   */
  const fetchServiceIntegration = async () => {
    if (!accountGroupId) return;
    
    setIsLoading(true);
    try {
      const result = await getKCloudIntegrationService.getKCloudIntegration({
        account_group_id: accountGroupId,
      });

      if(result){
        // snake_case から camelCase へ変換
        const formattedResult: KCloudIntegrationInfo = {
          accountGroupId: result.account_group_id,
          kikCode: result.kik_code,
          userId: result.user_id,
          uniClientCode: result.uni_client_code,
          clientCode: result.client_code,
          serviceType: result.service_type,
        };
        setServiceIntegrationInfo(formattedResult);
      } else {
        setServiceIntegrationInfo(null);
      }
      
    } catch (error) {
      console.error("サービス連携情報の取得に失敗しました:", error);
      setServiceIntegrationInfo(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * サービス連携解除
   */
  const disconnectKCLService = async () => {
    setIsLoading(true);
    try {
      await disconnectKCloudService.disconnectKCloud({
        account_group_id: accountGroupId,
      });
      
      // 解除後、状態をリセット
      setServiceIntegrationInfo(null);
    } catch (error) {
      console.log("disconnectKCLService:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * クラウド認証とクライアントリスト取得
   */
  const authenticateAndGetClients = async (
    kikCode: string, 
    userId: string, 
    password: string, 
    service: "kaikei" | "shokokai"
  ): Promise<ClientInfo[]> => {
    setIsLoading(true);
    try {
      const result = await KCloudAuthenticateService.login(service, {
        account_group_id: accountGroupId,
        kik_code: kikCode,
        user_id: userId,
        password: password,
      });

      if (result) {
        // snake_case から camelCase へ変換
        return result.map(client => ({
          uniClientCode: client.uni_client_code,
          clientCode: client.client_code,
          clientName: client.client_name,
          tantoFlag: client.tanto_flag,
        }));
      } else {
        return [];
      }
    } catch (error) {
      console.log("authenticateAndGetClients:", error);
      throw error;
      // return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 選択された顧問先情報を保存
   */
  const updateSelectedClient = async (
    clientInfo: ClientInfo,
    accountsList: { passbookName: string; mlinkAccountId: string; mlinkId: string; passbookType: string; lastAggregate: string }[],
    service: "kaikei" | "shokokai"
  ): Promise<void | "success"> => {
    setIsLoading(true);
    try {
      const apiClientInfo: ApiClientInfo = {
        uni_client_code: clientInfo.uniClientCode,
        client_code: clientInfo.clientCode,
        client_name: clientInfo.clientName,
        tanto_flag: clientInfo.tantoFlag,
      };
      const formattedAccountsList = accountsList.map(account => ({
        account_display_name: account.passbookName,
        mlink_account_id: account.mlinkAccountId,
        mlink_id: account.mlinkId,
        passbook_type: account.passbookType,
        last_aggregate: account.lastAggregate,
      }));

      await KCloudAuthenticateService.selectClient(service, {
        account_group_id: accountGroupId,
        client_info: apiClientInfo,
        accounts_list: formattedAccountsList
      });
      return "success";
    } catch (error) {
      console.error("顧問先情報の保存に失敗しました:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 初回マウント時と accountGroupId 変更時に連携情報を取得
  // useEffect(() => {
  //   fetchServiceIntegration();
  // }, [accountGroupId]);

  return {
    serviceIntegrationInfo,
    setServiceIntegrationInfo,
    isLoading,
    fetchServiceIntegration: errorHandledAsync(fetchServiceIntegration, setError),
    disconnectKCLService: errorHandledAsync(disconnectKCLService, setError),
    authenticateAndGetClients: errorHandledAsync(authenticateAndGetClients, setError),
    updateSelectedClient: errorHandledAsync(updateSelectedClient, setError),
  };
};
